import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { displayName } from "@/lib/patientFormat";
import { ScreenHead, ListCard, Skeletons, Avatar, DqMarker } from "./psPrimitives";
import { usePsClient, ClientPicker, LINK, useClientProviders } from "./psMasters";
import { useProviders } from "./psPrimitives";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const SHORT = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const SLOTS = [15, 20, 30, 45, 60], BUFS = [0, 5, 10, 15];
const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const lab12 = (t) => { const m = toMin(t), h = Math.floor(m / 60), mm = String(m % 60).padStart(2, "0"); return `${((h + 11) % 12) + 1}:${mm} ${h < 12 ? "AM" : "PM"}`; };
const OPTS = Array.from({ length: 24 * 4 }, (_, i) => toHHMM(i * 15)), END_OPTS = [...OPTS.slice(1), "23:59"];
const EMPTY_WEEK = Object.fromEntries(DAYS.map(([d]) => [d, []]));
const cfgKey = (cid) => ["provider-configs", cid];
const provName = (p) => displayName(p.provider_name || p.name || "");

export function useProviderConfigs(cid) {
  return useQuery({ queryKey: cfgKey(cid), enabled: !!cid, staleTime: 10_000,
    queryFn: async () => (await api.get(`/api/edi/televisit/provider-configs?client_id=${encodeURIComponent(cid)}`)).data?.configs ?? [] });
}
export const linkedProviders = (providers, cid) => providers.filter((p) => String(p.client_id ?? p.client_4digit_id ?? "") === String(cid));

export function summarize(cfg) {
  if (!cfg) return null;
  const wh = cfg.weekly_hours || {};
  const sig = (d) => (wh[d] || []).map((w) => { return `${lab12(w.start)}–${lab12(w.end)}`; }).join(", ");
  const parts = [];
  for (let i = 0; i < 7;) {
    const d = DAYS[i][0], s = sig(d);
    if (!s) { i++; continue; }
    let j = i; while (j + 1 < 7 && sig(DAYS[j + 1][0]) === s) j++;
    parts.push(`${SHORT[d]}${j > i ? `–${SHORT[DAYS[j][0]]}` : ""} ${s}`); i = j + 1;
  }
  if (!parts.length) parts.push("No working days");
  parts.push(`${cfg.slot_duration_minutes || 30}-min slots`);
  if (cfg.buffer_minutes) parts.push(`${cfg.buffer_minutes}-min buffer`);
  return parts.join(" · ");
}
const NOT_SET = "Not set · Mon–Fri 9–5, 30-min slots assumed";

const PS_CSS = `.av-sw{position:relative;width:44px;height:26px;border-radius:999px;flex:none;transition:background-color .15s}
.av-sw span{position:absolute;top:3px;width:20px;height:20px;border-radius:999px;background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:left .15s}
.av-sel{border:1px solid #E9E9EC;border-radius:8px;padding:5px 8px;font-size:13px;font-variant-numeric:tabular-nums;background:#fff;color:#141412;outline:none}
.av-sel:focus{border-color:#5856D6;box-shadow:0 0 0 3px rgba(88,86,214,.16)}
.av-win{display:inline-flex;align-items:center;gap:6px}
.av-x{width:24px;height:24px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;color:#9B9B97;opacity:0;transition:opacity .12s,background-color .12s,color .12s}
.av-win:hover .av-x,.av-win:focus-within .av-x{opacity:1}
.av-x:hover{background:#F1F1F4;color:#B3261E}
.av-day{display:grid;grid-template-columns:110px 52px 1fr;column-gap:12px;padding:12px 16px}
.av-wins{display:flex;flex-wrap:wrap;gap:8px 14px;align-items:center;min-height:34px}
@media (max-width:720px){.av-day{grid-template-columns:1fr 52px}.av-day .av-col{grid-column:1/-1;margin-top:8px}}
@media (prefers-reduced-motion:reduce){.av-sw,.av-sw span{transition:none}}`;

export function Sw({ on, onChange, testid, label }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} className="av-sw" style={{ background: on ? "#141412" : "#D6D6DB" }} data-testid={testid}><span style={{ left: on ? 21 : 3 }} /></button>;
}
export function RSeg({ items, value, onChange, testid }) {
  return (
    <div role="radiogroup" className="inline-flex items-center rounded-[10px] p-[3px] bg-[#F1F1F4]" data-testid={testid}>
      {items.map(([v, l]) => <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className="text-[12.5px] font-semibold px-3 py-[5px] rounded-[7px]" style={{ background: value === v ? "#fff" : "transparent", color: value === v ? "#141412" : "#6C6C69", boxShadow: value === v ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{l}</button>)}
    </div>
  );
}
export const ErrLine = ({ children, testid }) => <div className="flex items-center gap-2 text-[12.5px] font-semibold text-[#B3261E] mt-2" role="alert" data-testid={testid}><span className="w-[6px] h-[6px] rounded-full bg-[#B3261E] flex-none" />{children}</div>;
const GAPS = (one) => [["Time off & holidays", one ? "Days this provider is away." : "Days a provider is away, and practice-wide holidays."], ["Date-specific hours", one ? "Different hours for one date." : "Different hours for one date without changing the week."], ["Visit types this provider offers", one ? "Which visit types can be booked with this provider." : "Limit which visit types can be booked with each provider."]];
export function GapRows({ rows }) {
  return <div className="mt-8 flex flex-col gap-2.5" data-testid="av-gaps">{rows.map(([t, d]) => (
    <div key={t} className="flex items-center gap-3 rounded-[14px] px-4 py-3" style={{ border: "1.5px dashed #D4D4DA" }}>
      <div className="flex-1"><div className="text-[13px] font-semibold text-[var(--ink)]">{t}</div><div className="text-[12px] text-[var(--gray2)] mt-0.5">{d}</div></div><DqMarker n={0} />
    </div>))}</div>;
}

function validate(day) {
  const ws = [...day].sort((a, b) => toMin(a.start) - toMin(b.start));
  for (const w of ws) if (toMin(w.end) <= toMin(w.start)) return "Each window has to end after it starts.";
  for (let i = 1; i < ws.length; i++) if (toMin(ws[i].start) < toMin(ws[i - 1].end)) return "These windows overlap.";
  return "";
}

function ProviderHours({ cid, p, onBack, mode }) {
  const qc = useQueryClient();
  const { data: cfgs = [], isLoading } = useProviderConfigs(cid);
  const server = cfgs.find((c) => String(c.npi) === String(p.npi_number));
  const [cfg, setCfg] = useState(null);
  const [errs, setErrs] = useState({});
  const [stale, setStale] = useState(false);
  const [saveErr, setSaveErr] = useState(false);
  const base = useRef(null);
  useEffect(() => {
    if (isLoading || cfg) return;
    const start = server ? { ...server, weekly_hours: { ...EMPTY_WEEK, ...(server.weekly_hours || {}) } }
      : { weekly_hours: { ...EMPTY_WEEK, ...Object.fromEntries(["mon", "tue", "wed", "thu", "fri"].map((d) => [d, [{ start: "09:00", end: "17:00" }]])) }, slot_duration_minutes: 30, buffer_minutes: 0 };
    base.current = server ? JSON.stringify({ w: server.weekly_hours || {}, s: server.slot_duration_minutes, b: server.buffer_minutes }) : null;
    setCfg(start);
  }, [isLoading, server, cfg]);
  const save = async (next) => {
    const e = Object.fromEntries(DAYS.map(([d]) => [d, validate(next.weekly_hours[d] || [])]).filter(([, m]) => m));
    setErrs(e); setCfg(next);
    if (Object.keys(e).length) return;
    try {
      const cur = ((await api.get(`/api/edi/televisit/provider-configs?client_id=${encodeURIComponent(cid)}&npi=${p.npi_number}`)).data?.configs || [])[0];
      const curSig = cur ? JSON.stringify({ w: cur.weekly_hours || {}, s: cur.slot_duration_minutes, b: cur.buffer_minutes }) : null;
      if (curSig !== base.current) { setStale(true); return; }
      const body = { ...(cur || {}), client_id: String(cid), npi: String(p.npi_number), provider_name: p.provider_name, weekly_hours: next.weekly_hours, slot_duration_minutes: next.slot_duration_minutes, buffer_minutes: next.buffer_minutes };
      await api.put("/api/edi/televisit/provider-configs", body);
      base.current = JSON.stringify({ w: next.weekly_hours, s: next.slot_duration_minutes, b: next.buffer_minutes });
      setSaveErr(false); qc.invalidateQueries({ queryKey: cfgKey(cid) });
    } catch { setSaveErr(true); }
  };
  const reload = async () => { setStale(false); setCfg(null); setErrs({}); await qc.invalidateQueries({ queryKey: cfgKey(cid) }); };
  if (!cfg) return <Skeletons />;
  const setDay = (d, wins) => save({ ...cfg, weekly_hours: { ...cfg.weekly_hours, [d]: wins } });
  const addWin = (d) => {
    const ws = cfg.weekly_hours[d] || [], endLast = ws.length ? Math.max(...ws.map((w) => toMin(w.end))) : 8 * 60;
    const s = Math.min(endLast + 60, 23 * 60 + 30), e = Math.min(s + 120, 23 * 60 + 59);
    setDay(d, [...ws, { start: toHHMM(s), end: toHHMM(e) }]);
  };
  const copyMon = () => { const m = cfg.weekly_hours.mon || []; save({ ...cfg, weekly_hours: { ...cfg.weekly_hours, ...Object.fromEntries(["tue", "wed", "thu", "fri"].map((d) => [d, m.map((w) => ({ ...w }))])) } }); };
  const sum = server ? summarize({ ...cfg }) : null;
  return (
    <div className="fade-up" data-testid="av-provider-page">
      <style>{PS_CSS}</style>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] mb-3" data-testid="av-back"><ChevronLeft size={14} /> Availability</button>
      <h2 className="text-[22px] font-bold tracking-[-.01em] text-[var(--ink)]">{provName(p)}</h2>
      <div className="text-[13px] text-[var(--gray2)] mt-1.5">NPI {p.npi_number}</div>
      <div className={`text-[13.5px] mt-1.5 ${sum ? "text-[var(--gray2)]" : "text-[var(--gray3)]"}`} data-testid="av-summary">{sum || NOT_SET}</div>
      {stale ? <ErrLine testid="av-stale">These hours changed after you opened this page.<button type="button" className={`${LINK} ml-1.5`} onClick={reload} data-testid="av-reload">Reload</button></ErrLine> : null}
      {saveErr ? <ErrLine testid="av-save-error">Couldn't save. Change anything to try again.</ErrLine> : null}
      {!server ? <div className="text-[12.5px] text-[var(--gray3)] mt-3" data-testid="av-notset-note">Not set yet. Booking assumes these hours until you change one.</div> : null}
      <div className="text-[13px] font-semibold text-[var(--gray2)] mt-6 mb-2.5">Weekly hours</div>
      <ListCard testid="av-days">
        {DAYS.map(([d, label], i) => {
          const ws = cfg.weekly_hours[d] || [], on = ws.length > 0;
          return (
            <div key={d} className={`av-day ${i ? "border-t border-[#E9E9EC]" : ""}`} data-testid={`av-day-${d}`}>
              <span className="text-[14px] font-semibold text-[var(--ink)] pt-[6px]">{label}</span>
              <span className="pt-1"><Sw on={on} label={`${label} working`} onChange={(v) => setDay(d, v ? [{ start: "09:00", end: "17:00" }] : [])} testid={`av-sw-${d}`} /></span>
              <div className="av-col min-w-0">
                {on ? (
                  <div className="av-wins">
                    {ws.map((w, wi) => (
                      <span key={wi} className="av-win" data-testid={`av-win-${d}-${wi}`}>
                        <select className="av-sel" value={w.start} aria-label={`${label} window ${wi + 1} start`} onChange={(e) => setDay(d, ws.map((x, k) => (k === wi ? { ...x, start: e.target.value } : x)))} data-testid={`av-start-${d}-${wi}`}>{OPTS.map((o) => <option key={o} value={o}>{lab12(o)}</option>)}</select>
                        <span className="text-[var(--gray3)]">–</span>
                        <select className="av-sel" value={w.end} aria-label={`${label} window ${wi + 1} end`} onChange={(e) => setDay(d, ws.map((x, k) => (k === wi ? { ...x, end: e.target.value } : x)))} data-testid={`av-end-${d}-${wi}`}>{END_OPTS.map((o) => <option key={o} value={o}>{lab12(o)}</option>)}</select>
                        <button type="button" className="av-x" aria-label={`Remove ${label} window ${wi + 1}`} onClick={() => setDay(d, ws.filter((_, k) => k !== wi))} data-testid={`av-x-${d}-${wi}`}><X size={13} /></button>
                      </span>
                    ))}
                    <button type="button" className="text-[12.5px] font-semibold text-[#4A4AC2] hover:underline" onClick={() => addWin(d)} data-testid={`av-add-${d}`}>+ Add window</button>
                  </div>
                ) : <div className="text-[13px] text-[var(--gray3)] pt-[7px]">Not working</div>}
                {d === "mon" && on ? <button type="button" className="text-[12.5px] font-semibold text-[#4A4AC2] hover:underline mt-2" onClick={copyMon} data-testid="av-copy-mon">Copy Monday to weekdays</button> : null}
                {errs[d] ? <ErrLine testid={`av-err-${d}`}>{errs[d]}</ErrLine> : null}
              </div>
            </div>
          );
        })}
      </ListCard>
      <div className="mt-4">
        <div className="flex items-center justify-between flex-wrap gap-4 py-3.5 border-b border-[#E9E9EC]">
          <div><div className="text-[14px] font-semibold text-[var(--ink)]">Slot length</div><div className="text-[12.5px] text-[var(--gray3)]">Every visit is booked at this length.</div></div>
          <RSeg items={SLOTS.map((m) => [m, `${m} min`])} value={cfg.slot_duration_minutes || 30} onChange={(v) => save({ ...cfg, slot_duration_minutes: v })} testid="av-slot" />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-4 py-3.5">
          <div><div className="text-[14px] font-semibold text-[var(--ink)]">Buffer</div><div className="text-[12.5px] text-[var(--gray3)]">Kept free after each visit.</div></div>
          <RSeg items={BUFS.map((m) => [m, m ? `${m} min` : "None"])} value={cfg.buffer_minutes || 0} onChange={(v) => save({ ...cfg, buffer_minutes: v })} testid="av-buffer" />
        </div>
      </div>
      {mode === "full" ? <GapRows rows={GAPS(true)} /> : null}
    </div>
  );
}

export function ProviderList({ eyebrow, title, lede, metaOf, onPick, onGoProviders, mode, gaps, testid }) {
  const pc = usePsClient(); const { cid } = pc;
  const { data: providers = [], isLoading } = useClientProviders();
  const { data: cfgs = [], isLoading: l2 } = useProviderConfigs(cid);
  const list = useMemo(() => linkedProviders(providers, cid), [providers, cid]);
  return (
    <div className="fade-up" data-testid={testid}>
      <style>{PS_CSS}</style>
      <ScreenHead eyebrow={eyebrow} title={title} lede={lede} badge="Optional" />
      {pc.isLoading || isLoading ? <Skeletons /> : <>
        <ClientPicker {...pc} />
        {l2 ? <Skeletons /> : !list.length ? (
          <div className="rounded-[14px] px-4 py-6 text-center text-[13px] text-[var(--gray2)]" style={{ border: "1.5px dashed #D4D4DA" }} data-testid={`${testid}-empty`}>No providers are linked to this client yet. <button type="button" className={LINK} onClick={onGoProviders} data-testid={`${testid}-goproviders`}>Go to Providers</button></div>
        ) : <>
          <div className="text-[13px] text-[var(--gray2)] mb-2.5">{list.length} provider{list.length === 1 ? "" : "s"}</div>
          <ListCard testid={`${testid}-list`}>
            {list.map((p, i) => { const m = metaOf(cfgs.find((c) => String(c.npi) === String(p.npi_number))); return (
              <button key={p.npi_number} onClick={() => onPick(p)} className={`w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-black/[.03] transition ${i ? "border-t border-[#E9E9EC]" : ""}`} data-testid={`${testid}-row-${p.npi_number}`}>
                <Avatar text={provName(p)} />
                <div className="flex-1 min-w-0"><div className="text-[13px] font-semibold text-[var(--ink)] truncate">{provName(p)}</div>
                  <div className={`text-[11.5px] mt-0.5 truncate ${m.faint ? "text-[var(--gray3)]" : "text-[var(--gray2)]"}`} data-testid={`${testid}-meta-${p.npi_number}`}>{m.text}</div></div>
                <ChevronRight size={16} className="text-[var(--gray3)]" />
              </button>); })}
          </ListCard>
        </>}
        {mode === "full" ? <GapRows rows={gaps} /> : null}
      </>}
    </div>
  );
}

export function AvailabilityScreen({ mode, initialNpi, onGoProviders }) {
  const { cid } = usePsClient();
  const { data: providers = [] } = useClientProviders();
  const [npi, setNpi] = useState(initialNpi || null);
  useEffect(() => { setNpi(initialNpi || null); }, [initialNpi]);
  const p = npi ? providers.find((x) => String(x.npi_number) === String(npi)) : null;
  if (p && cid) return <ProviderHours key={`${cid}-${npi}`} cid={cid} p={p} mode={mode} onBack={() => setNpi(null)} />;
  return <ProviderList testid="av" eyebrow="Scheduling (telehealth) · Step 1 of 2" title="Availability" mode={mode} gaps={GAPS(false)} onGoProviders={onGoProviders}
    lede="When each provider can be booked. Telemedicine offers only open slots inside these hours."
    metaOf={(c) => (c ? { text: summarize(c) } : { text: NOT_SET, faint: true })} onPick={(x) => setNpi(x.npi_number)} />;
}
